import * as React from 'react';
import * as styles from './styles.css';
import { Dropdown, Select, SelectModal } from './components';
import { useDropdownFilter, useOutsideClick, useSelectOption } from './utils';
import { IDropdownOption, TOptionValues } from '../../types/dropdown';
import { Typography } from '../Typography';
import { useDeviceType } from '@cian/ui-kit';
import cn from 'clsx';

interface IComboboxProps {
  options: IDropdownOption[];
  label: string;
  isMultiselect?: boolean;
  handleSelect: (name: string, selectedOptions: string[]) => void;
  name: string;
  selectedOptions: TOptionValues;
  handleReset: (optionName: string) => void;
}

export const Combobox: React.FC<IComboboxProps> = ({
  name,
  options = [],
  label,
  isMultiselect = false,
  handleSelect,
  selectedOptions = [],
  handleReset,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [dropdownList, setDropdownList] = React.useState(options);
  const [searchQuery, setSearchQuery] = React.useState('');
  const dropdownRef = React.useRef(null);
  const deviceType = useDeviceType();

  const { checkIsSelected, onOptionSelect } = useSelectOption(handleSelect, name, selectedOptions);

  const isNeedToShowLabel = Boolean(selectedOptions.length) || Boolean(searchQuery);

  const selectedOptionLabels = options
    .reduce((labels: string[], option) => {
      if (option.value && selectedOptions.includes(option.value)) {
        labels.push(option.label);
      }

      return labels;
    }, [])
    .join('; ');

  const placeholder = selectedOptions.length ? selectedOptionLabels : label;

  const handleFilter = useDropdownFilter();

  const handleChange = (value: string) => {
    setSearchQuery(value);
    handleFilter(value, options, setDropdownList);
  };

  const handleHideDropdown = () => {
    setIsDropdownOpen(false);
    setSearchQuery('');
    handleFilter('', options, setDropdownList);
  };

  useOutsideClick(dropdownRef, deviceType === 'desktop' && isDropdownOpen, handleHideDropdown);

  return (
    <div
      className={cn(styles['dropdown'], styles[deviceType], { [styles.padded]: isNeedToShowLabel })}
      ref={dropdownRef}
    >
      {isNeedToShowLabel && (
        <div className={styles['label']}>
          <Typography.Text level={5} color="secondary">
            {label}
          </Typography.Text>
        </div>
      )}
      <Select
        isDropdownOpen={isDropdownOpen}
        value={searchQuery}
        placeholder={placeholder}
        onChange={e => handleChange(e.target.value.trim())}
        canResetFilter={Boolean(selectedOptions.length)}
        handleOpenDropdown={setIsDropdownOpen}
        handleReset={() => handleReset(name)}
      />
      {isDropdownOpen && deviceType === 'desktop' ? (
        <Dropdown
          checkIsOptionSelected={checkIsSelected}
          list={dropdownList}
          isMultiselect={isMultiselect}
          handleSelect={onOptionSelect}
        />
      ) : (
        <SelectModal
          checkIsOptionSelected={checkIsSelected}
          list={dropdownList}
          isMultiselect={isMultiselect}
          handleSelect={onOptionSelect}
          isOpen={isDropdownOpen}
          title={label}
          handleClose={handleHideDropdown}
          handleReset={() => handleReset(name)}
        />
      )}
    </div>
  );
};
